/**
 * Shell 的介面文字。
 *
 * 語言活在 `$sys/locale` 這條 topic 上——不是在一個模組變數裡，也不是每個地方
 * 各自去問 `navigator.language`。桌面上除了 shell 還有十幾個各自來源的 iframe，
 * 各自猜的話使用者換語言會換到一半:有些跟著改、有些不動，而那看起來像壞掉。
 *
 * 節點的 `description` 不在這裡。protocol.md §6 說它是「agent 唯一的理解來源」
 * ——那是寫給模型讀的，而模型不需要它翻譯。兩種語言都送只會讓每個 app 的 $meta
 * 變兩倍大。
 */

export type Locale = 'zh-Hant' | 'en'

export type ShellKey = keyof (typeof STRINGS)['zh-Hant']

const STRINGS = {
  'zh-Hant': {
    // 視窗外框
    howToUse: '這個工具怎麼用',
    settings: '設定',
    toFront: '移到頂層',
    toBack: '移到底層',
    minimise: '最小化',
    maximise: '最大化',
    restore: '還原',
    close: '關閉',
    lockPosition: '鎖住位置。解鎖在畫面下方的 🔒',
    reload: '重新載入',
    noResponse: '這個 app 沒有回應',
    ended: '這個 app 已結束',

    // 設定面板
    noSettings: '這個 app 沒有可以調整的設定。',
    notReady: '這個 app 還沒有註冊完成。',
    noTopics: '這個 app 沒有對外提供任何 topic——它只有畫面。',
    readOnly: '唯讀',
    callable: '可呼叫',
    writable: '可寫',
    needsConfirm: '需確認',
    language: '語言',

    // 啟動器
    searchTools: '搜尋工具…',
    catalogEmpty: '目錄裡沒有可以開的工具',

    // dock
    noWindows: '桌面上沒有視窗',
    unlock: '🔓 解鎖',
    lock: '鎖住',
    lockWindows: '鎖住視窗的位置',
    notPaired: '未配對',
    linkConnected: '已連線',
    linkConnecting: '連線中',
    linkOffline: '已斷線',
    linkUnpaired: '未配對',
    addWidget: '新增元件',
    saveSaved: '已儲存',
    saveSaving: '儲存中',
    saveError: '沒有同步',
    workspace: '工作空間',
    pairFailed: '這台桌面沒有配對成功，QSyn 的代理人接不到它',
    switchDesktop: '換一個桌面',
    help: '說明文件',
    helpClose: '關閉說明',

    // 版面板型
    layoutHalves: '左右各半',
    layoutThirds: '三欄',
    layoutQuad: '四宮格',
    layoutNine: '九宮格',
    layoutMainSide: '主畫面 + 側欄',

    // JSON 編輯器
    jsonBadFormat: 'JSON 格式錯誤',
    jsonSingleQuote: '。JSON 的字串要用雙引號，不能用單引號',
    jsonBareKey: '。JSON 的鍵名要用雙引號括起來：{"t":1}，不是 {t:1}',
    jsonTrailingComma: '。結尾多了一個逗號',

    // 桌面工作階段 app（畫面上看得到的部分；description 仍是中文）
    sessionTitle: '桌面工作階段',
    sessionSummary: '桌面本身的存檔控制。可立即存檔或清除存檔回到預設桌面。',
    sessionGroupData: '存檔狀態',
    sessionGroupCmd: '存檔操作',
  },
  en: {
    howToUse: 'What this tool does',
    settings: 'Settings',
    toFront: 'Bring to front',
    toBack: 'Send to back',
    minimise: 'Minimise',
    maximise: 'Maximise',
    restore: 'Restore',
    close: 'Close',
    lockPosition: 'Lock in place. Unlock from the 🔒 at the bottom of the screen',
    reload: 'Reload',
    noResponse: 'This app is not responding',
    ended: 'This app has stopped',

    noSettings: 'This app has nothing to configure.',
    notReady: 'This app has not finished registering.',
    noTopics: 'This app publishes no topics — it is only a display.',
    readOnly: 'read-only',
    callable: 'callable',
    writable: 'writable',
    needsConfirm: 'needs confirmation',
    language: 'Language',

    searchTools: 'Search tools…',
    catalogEmpty: 'No tools in the catalogue',

    noWindows: 'No windows on this desktop',
    unlock: '🔓 Unlock',
    lock: 'Lock',
    lockWindows: 'Lock windows in place',
    notPaired: 'not paired',
    linkConnected: 'Connected',
    linkConnecting: 'Connecting',
    linkOffline: 'Disconnected',
    linkUnpaired: 'Not paired',
    addWidget: 'Add a widget',
    saveSaved: 'Saved',
    saveSaving: 'Saving',
    saveError: 'Not synced',
    workspace: 'Workspace',
    pairFailed: 'This desktop did not pair, so QSyn agents cannot reach it',
    switchDesktop: 'Switch desktop',
    help: 'Help',
    helpClose: 'Close help',

    layoutHalves: 'Two halves',
    layoutThirds: 'Three columns',
    layoutQuad: 'Four panes',
    layoutNine: 'Nine panes',
    layoutMainSide: 'Main + sidebar',

    jsonBadFormat: 'Invalid JSON',
    jsonSingleQuote: '. JSON strings use double quotes, not single ones',
    jsonBareKey: '. JSON keys must be quoted: {"t":1}, not {t:1}',
    jsonTrailingComma: '. There is a trailing comma',

    sessionTitle: 'Desktop session',
    sessionSummary:
      "Saving and restoring this desktop. Save now, or clear the save and return to the default desktop.",
    sessionGroupData: 'Saved state',
    sessionGroupCmd: 'Actions',
  },
} as const

/**
 * 一個綁在某種語言上的查表函式。
 *
 * 查不到就退回中文，再查不到就回鍵名。畫面上出現一個鍵名很醜，但比出現空白好
 * ——空白看起來像元件壞了，鍵名看起來像少翻了一句，而那兩件事要修的地方不同。
 */
export function translator(locale: Locale): (key: ShellKey) => string {
  return (key) => STRINGS[locale][key] ?? STRINGS['zh-Hant'][key] ?? key
}

export function isLocale(value: unknown): value is Locale {
  return value === 'zh-Hant' || value === 'en'
}

/** 語言選單上的名字。每一種都用它自己的語言寫——那是找得到自己那一行的方式。 */
export const LOCALE_NAMES: Record<Locale, string> = {
  'zh-Hant': '繁體中文',
  en: 'English',
}
